import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import {
  LayoutMetadata,
  parseLayoutMetadataFile,
} from '../utils/parse-layout-metadata-file';

export type { LayoutMetadata };

// Reads html-layout-library's metadata.json — every current layout's
// design-time base dimensions and behavior flags — from the layouts folder
// the layout components render from (environment.LAYOUT_METADATA_URL).
// The file is small, so it is fetched once and every lookup is answered from
// memory; see preload() for how to keep that fetch off the click path.
//
// This must stay a plain GET with no custom request headers: the bucket's
// CORS policy answers simple cross-origin GETs only (a preflight for a
// custom header is not approved).
//
// A layout missing from the file, a malformed entry, an unsupported
// schemaVersion, or a failed fetch all read as `null` ("no data"), which
// callers treat as "use the default box".
@Injectable({
  providedIn: 'root',
})
export class LayoutMetadataService {
  private static readonly REQUEST_TIMEOUT_MS = 5000;

  // Held for the life of the editor session. Only set from a genuine 200
  // response — including one that turns out to have no usable entries, since
  // fetching it again would return the same thing. A network failure, 404 or
  // timeout is never stored, so a later lookup gets a fresh chance once the
  // problem clears (e.g. the file being deployed mid-session).
  private layouts: Map<string, LayoutMetadata> | null = null;

  // The in-flight fetch. Concurrent callers (the editor's preload and a
  // layout add or switch landing before it resolves) all join this one
  // request instead of starting their own. Cleared once it settles.
  private pending: Promise<Map<string, LayoutMetadata> | null> | null = null;

  private readonly httpClient = inject(HttpClient);

  // Starts loading the file without waiting for it. The editor calls this
  // once when it opens, so by the time a layout is added or switched the file
  // is already in memory and sizing never waits on the network. Safe to call
  // repeatedly; it never rejects.
  preload(): void {
    void this._load();
  }

  // Resolves with a layoutId -> metadata map covering every requested id.
  // Never rejects: an id with no usable data (absent, malformed, or the file
  // could not be loaded) comes back null, so callers handle all of those the
  // same way.
  async getMetadata(
    layoutIds: string[],
  ): Promise<Record<string, LayoutMetadata | null>> {
    const uniqueIds = Array.from(new Set(layoutIds.filter(Boolean)));
    const result: Record<string, LayoutMetadata | null> = {};

    if (uniqueIds.length === 0) {
      return result;
    }

    const layouts = await this._load();

    uniqueIds.forEach((layoutId) => {
      result[layoutId] = layouts?.get(layoutId) ?? null;
    });

    return result;
  }

  private _load(): Promise<Map<string, LayoutMetadata> | null> {
    if (this.layouts) {
      return Promise.resolve(this.layouts);
    }

    if (!this.pending) {
      this.pending = this._fetch().then((layouts) => {
        this.pending = null;
        if (layouts) {
          this.layouts = layouts;
        }
        return layouts;
      });
    }

    return this.pending;
  }

  // Resolves with the parsed layouts, or null when the file could not be
  // retrieved. Never rejects.
  private async _fetch(): Promise<Map<string, LayoutMetadata> | null> {
    try {
      const body = await firstValueFrom(
        this.httpClient
          .get<unknown>(environment.LAYOUT_METADATA_URL)
          .pipe(timeout(LayoutMetadataService.REQUEST_TIMEOUT_MS)),
      );

      const { layouts, problems } = parseLayoutMetadataFile(body);
      if (problems.length > 0) {
        console.warn('Layout metadata was only partially usable.', problems);
      }

      return layouts;
    } catch (err) {
      console.error('Failed to fetch layout metadata.', err);
      return null;
    }
  }
}
